// Catalog.ID Client — Trust API
// ============================================================================
// Trust expressions, scores, endorsements, vouching, and Merkle verification.

import { api } from './client';
import type {
    TrustExpressionSummary,
    ReceivedTrustResponse,
    TrustScoreResponse,
    PersonalizedTrustResponse,
    MerkleTreeResponse,
    PreTrustedPeer,
    VouchingRequirement,
    VouchingStatusResponse,
    EndorsementSummary,
    MyEndorsementSummary,
    MyClaimEndorsement,
    VerifiablePersonalizedResult,
    MerkleInclusionProof,
} from './types';

// ── Trust Expressions ──

export async function expressTrust(
    targetCid: string,
    score: number,
    signature: string,
    commitment: string,
    nonce: string,
    subgraphVisible?: boolean,
): Promise<{ trustExpressionId: number; updated: boolean }> {
    return api.post(`/api/trust/${targetCid}`, {
        score, signature, commitment, nonce, subgraphVisible,
    });
}

export async function removeTrust(targetCid: string): Promise<void> {
    return api.del(`/api/trust/${targetCid}`);
}

export async function getGivenTrust(): Promise<TrustExpressionSummary[]> {
    return api.get('/api/trust/my/given');
}

export async function getReceivedTrust(): Promise<ReceivedTrustResponse> {
    return api.get('/api/trust/my/received');
}

export async function updateSubgraphVisibility(visible: boolean): Promise<void> {
    return api.put('/api/trust/my/subgraph-visibility', { visible });
}

// ── Trust Scores ──

export async function getTrustScore(cid: string): Promise<TrustScoreResponse> {
    return api.get(`/api/trust/score/${cid}`);
}

export async function getTrustScores(cids: string[]): Promise<TrustScoreResponse[]> {
    return api.get(`/api/trust/scores?cids=${cids.join(',')}`);
}

// ── Personalized Trust ──

export async function getPersonalizedScore(cid: string): Promise<PersonalizedTrustResponse> {
    return api.get(`/api/trust/personal/${cid}`);
}

export async function getPersonalizedScores(cids: string[]): Promise<PersonalizedTrustResponse[]> {
    return api.get(`/api/trust/personal?cids=${cids.join(',')}`);
}

// ── Merkle Verification ──

export async function getMerkleTree(): Promise<MerkleTreeResponse> {
    return api.get('/api/trust/tree');
}

// ── Pre-trusted Peers (Operator) ──

export async function listPreTrustedPeers(): Promise<PreTrustedPeer[]> {
    return api.get('/api/trust/pretrusted');
}

export async function addPreTrustedPeer(cid: string, weight: number): Promise<{ trustPreTrustedPeerId: number }> {
    return api.post('/api/trust/pretrusted', { cid, weight });
}

export async function removePreTrustedPeer(cid: string): Promise<void> {
    return api.del(`/api/trust/pretrusted/${cid}`);
}

// ── Vouching Requirements ──

export async function listVouchingRequirements(): Promise<VouchingRequirement[]> {
    return api.get('/api/trust/requirements');
}

export async function updateVouchingRequirement(
    claimType: string,
    minEndorsementCount: number,
    minEndorserTier: number,
    operatorCanWaive: boolean,
): Promise<void> {
    return api.put(`/api/trust/requirements/${claimType}`, {
        minEndorsementCount, minEndorserTier, operatorCanWaive,
    });
}

// ── Endorsements ──

export async function endorseClaim(
    claimUuid: string,
    endorsement: boolean,
    signature: string,
): Promise<{ claimEndorsementId: number }> {
    return api.post(`/api/claims/${claimUuid}/endorse`, { endorsement, signature });
}

export async function removeEndorsement(claimUuid: string): Promise<void> {
    return api.del(`/api/claims/${claimUuid}/endorse`);
}

export async function getEndorsements(claimUuid: string): Promise<EndorsementSummary[]> {
    return api.get(`/api/claims/${claimUuid}/endorsements`);
}

export async function getMyEndorsements(): Promise<MyEndorsementSummary[]> {
    return api.get('/api/claims/my/endorsements');
}

export async function getMyEndorsement(claimUuid: string): Promise<MyClaimEndorsement | null> {
    try {
        return await api.get(`/api/claims/${claimUuid}/my-endorsement`);
    } catch {
        return null; // 404 — no endorsement exists
    }
}

export async function getVouchingStatus(claimUuid: string): Promise<VouchingStatusResponse> {
    return api.get(`/api/claims/${claimUuid}/vouching-status`);
}

// ── Verification ──

export async function getVerifiablePersonalizedScore(cid: string, anchors?: string): Promise<VerifiablePersonalizedResult> {
    const params = anchors ? `?anchors=${anchors}` : '';
    return api.get(`/api/trust/personal/${cid}/verify${params}`);
}

export async function getGivenInclusionProofs(): Promise<MerkleInclusionProof[]> {
    return api.get('/api/trust/verify/given');
}

export async function getReceivedInclusionProofs(): Promise<MerkleInclusionProof[]> {
    return api.get('/api/trust/verify/received');
}
